import { useState } from "react";
import { TableColumn } from "react-data-table-component";
import { useTranslation } from "react-i18next";
import { useDeleteReadReceipt } from "../../../../api/DocumentApi";
import getIcon, { Icon, IconSize } from "../../../../assets/Icons/IconClassNames";
import ClickableIconButton from "../../../../components/ClickableIconButton/ClickableIconButton";
import PaginatedDataTableClientSide from "../../../../components/DataTableComponents/PaginatedDataTable";
import SimpleConfirmModal from "../../../../components/ModalComponents/SimpleConfirmModal";
import { Role } from "../../../../core/enums/enums";
import { DateToYYYYMMDDHHMM } from "../../../../core/helpers/dates/datesHelper";
import { IExtendedDocumentsReadReceipt } from "../../../../core/interfaces/document";
import { useGetUser } from "../../../../hooks/useAuth";


const ReadReceiptsTable = ({ readReceipts, companyId }: { readReceipts: IExtendedDocumentsReadReceipt[], companyId: string }) => {
    const { t } = useTranslation();
    const [filterText, setFilterText] = useState("");
    const user = useGetUser();
    const [showRemoveReadReceiptModal, setShowRemoveReadReceiptModal] = useState(false);
    const [selectedReadReceipt, setSelectedReadReceipt] = useState<IExtendedDocumentsReadReceipt | null>(null);
    const [remove, isRemoving] = useDeleteReadReceipt(selectedReadReceipt?.id ?? '', selectedReadReceipt?.document?.id ?? '', companyId);

    const [currentReadReceipts, setCurrentReadReceipts] = useState<IExtendedDocumentsReadReceipt[]>(readReceipts);

    const columns: TableColumn<IExtendedDocumentsReadReceipt>[] = [
        {
            name: t('ReadReceiptTable.PERSON_1'),
            selector: row => row.user.name,
            sortable: true,
            maxWidth: "12rem",
        },
        {
            name: t('ReadReceiptTable.DOCUMENT_1'),
            selector: row => row.document.name,
            sortable: true,
            maxWidth: "12rem",
        },
        {
            name: t('ReadReceiptTable.SENT_1'),
            selector: row => DateToYYYYMMDDHHMM(row.createdAt!),
            sortable: true,
            maxWidth: "10rem",
        },
        {
            name: t('ReadReceiptTable.REQUESTED_BY_1'),
            selector: row => row.requestedBy.name,
            sortable: true,
            maxWidth: "12rem",
        }
    ];

    user.role === Role.SUPERADMIN && columns.push({
        name: '',
        right: true,
        width: "4rem",
        cell: row => (
            <ClickableIconButton
                disabled={false}
                buttonType={"CANCEL"}
                icon={getIcon(Icon.REMOVE, IconSize.LARGE)}
                onClick={() => {
                    setSelectedReadReceipt(row);
                    setShowRemoveReadReceiptModal(true);
                }}
                title={t('ReadReceiptTable.DELETE_READ_RECEIPT_1')}
            />
        )
    })

    const filteredItems = currentReadReceipts.filter(
        item => (
            (item.user && item.user.name.trim().toLowerCase().includes(filterText.trim().toLowerCase())) ||
            (item.document && item.document.name.toLowerCase().includes(filterText.toLowerCase())) ||
            (item.createdAt && item.createdAt.toLowerCase().includes(filterText.toLowerCase())) ||
            (item.requestedBy && item.requestedBy.name.toLowerCase().includes(filterText.toLowerCase())) ||
            (filterText.includes(' ') &&
                (item.user.name.split(' ')[0].toLowerCase().includes(filterText.split(' ')[0].toLowerCase())) &&
                (item.user.name.split(' ')[1].toLowerCase().includes(filterText.split(' ')[1].toLowerCase()))
            ) ||
            (filterText.includes(' ') &&
                (item.requestedBy.name.split(' ')[0].toLowerCase().includes(filterText.split(' ')[0].toLowerCase())) &&
                (item.requestedBy.name.split(' ')[1].toLowerCase().includes(filterText.split(' ')[1].toLowerCase()))
            )
        )
    )

    const deleteReadReceipt = async () => {
        if (!selectedReadReceipt)
            return;

        remove().then(_ => {
            setCurrentReadReceipts(prev => prev.filter(r => r.id !== selectedReadReceipt.id));
            setShowRemoveReadReceiptModal(false);
        });
    }

    return <>
        <PaginatedDataTableClientSide<IExtendedDocumentsReadReceipt>
            data={filteredItems ?? []}
            columns={columns}
            progressPending={false}
            withFilter
            filterText={filterText}
            setFilterText={(prev) => setFilterText(prev)}
        />
        <SimpleConfirmModal
            show={showRemoveReadReceiptModal}
            header={<>{t('ReadReceiptTable.DELETE_READ_RECEIPT_1')}</>}
            text={<>{t('ReadReceiptTable.CONFIRM_DELETETION_1')}</>}
            onClickOKText={t('ReadReceiptTable.YES_1')}
            onClickOK={deleteReadReceipt}
            onClickCancelText={t('ReadReceiptTable.NO_1')}
            onClickCancel={() => {
                setSelectedReadReceipt(null);
                setShowRemoveReadReceiptModal(false);
            }}
        />
    </>
}

export default ReadReceiptsTable
